import React, { useContext, useEffect, useMemo, useState } from 'react';
import Header from '../../../components/Common/Header/Header';
import ContentApp from '../../../components/ContentApp';
import Money from '../../../components/Common/Money/Money';
import { useSelector } from 'react-redux';
import { WelletContext } from '../../../context/WelletContext';
import { fetchShow } from '../../../actions/appActions';
import GeneralSelectorPicker from '../../../components/SelectorPicker/GeneralSelectorPicker';
import Button from '../../../components/Common/Button/Button';
import styles from './Form.module.css';
import ReservationCheckCustomerInfo from '../../../components/ReservationCheck/ReservationCheckCustomerInfo/ReservationCheckCustomerInfo';
import useForm from '../../../helpers/useForm';
import { useTranslation } from 'react-i18next';
import { isValidPhoneNumber } from 'react-phone-number-input';
import ButtonProccessing, {
  ButtonStatus,
} from '../../../components/Common/ButtonProccessing/ButtonProccessing';
import InputFormBorder from '../../../components/Common/InputFormBorder/InputFormBorder';
import { putReservation } from '../../../services/reservations/reservationsService';
import { useHistory } from 'react-router-dom';
import { CircleSpinner } from 'react-spinners-kit';
import { Numpad } from '../../../components/Numpad/Numpad';
import { getDateFromOffset } from '../../../helpers/dates';
import { format } from 'date-fns';

const Form = () => {
  const app = useSelector((state) => state.app);
  const welletContext = useContext(WelletContext);
  const history = useHistory();
  const { t } = useTranslation();
  const [phoneError, setPhoneError] = useState('');
  const countryCode = useSelector((state) => state.app.currentMarket?.countryCode);
  const currency = useSelector((state) => state.currency.selected);
  const currencies = useSelector((state) => state.currency.all);
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState(null);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [selectedTime, setSelectedTime] = useState(1440);
  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const [units, setUnits] = useState(null);
  const [amount, setAmount] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [errorAmount, setErrorAmount] = useState('');
  const [errorUnit, setErrorUnit] = useState('');
  const [btnStatus, setBtnStatus] = useState(ButtonStatus.Normal);
  const [reservationDate, setReservation] = useState(new Date());
  const canMakeReservations = useSelector((state) => state.associates.user?.canMakeReservations);

  const currencyOption = (currency) => {
    return {
      value: currency.shortForm,
      label: currency.name,
      isDefault: currency.isDefault,
      symbol: currency.symbol,
    };
  };

  const currenciesOptions = useMemo(() => {
    return currencies?.map((item) => currencyOption(item)) || [];
  }, [currencies]);

  const defaultCurrencyOption = useMemo(() => {
    return currenciesOptions.find((c) => c.isDefault) || null;
  }, [currenciesOptions]);

  const getUnits = async () => {
    try {
      const result = await fetchShow(app.language, '', 1, 100, welletContext, '', '');

      const formattedUnits = result.shows.map((show) => ({
        img: show.logoUrl,
        value: show.id,
        label: show.name,
        additionalCharges: show.additionalCharges || [],
      }));
      setUnits(formattedUnits);
    } catch (error) {
      console.error(error);
    }
    setLoading(false);
  };

  const handleUnitChange = (selectedOption) => {
    setSelectedUnit(selectedOption);
  };

  const handleExpireTime = (selectedOption) => {
    setSelectedTime(selectedOption.value);
  };

  const resetForm = () => {
    setShowForm(false);
    setSelectedUnit(null);
    setAmount(null);
  };

  const getShow = async () => {
    try {
      const result = await welletContext.apis.tickets.get(`/shows/get/`, {
        params: {
          showId: selectedUnit.value,
          lang: app.language,
        },
      });

      setReservation(new Date(getDateFromOffset(result.data.utcOffset)));
    } catch (error) {
      console.error(error);
    }
  };

  function validate(values) {
    let errors = {};

    if (!values.customerName) {
      errors.customerName = t('nameError.EMPTY_NAME');
    } else if (/\d/.test(values.customerName)) {
      errors.customerName = t('nameError.NOT_NUMBERS');
    }

    if (!values.customerLastName) {
      errors.customerLastName = t('nameError.EMPTY_LAST_NAME');
    } else if (/\d/.test(values.customerLastName)) {
      errors.customerLastName = t('nameError.NOT_NUMBERS');
    }

    if (!values.phone) {
      errors.phone = t('phoneError.EMPTY_PHONE_NUMBER');
      setPhoneError(errors.phone);
    } else {
      setPhoneError('');
    }

    if (values.phone && !isValidPhoneNumber(values.phone)) {
      errors.phone = t('phoneError.INVALID_PHONE_NUMBER');
      setPhoneError(errors.phone);
    }

    if (!values.description) {
      errors.description = t('errorDescription');
    }

    if (!values.externalRefCode) {
      errors.externalRefCode = t('errorInternalReference');
    }

    if (values.email && !/\S+@\S+\.\S+/.test(values.email)) {
      errors.email = t('emailError.INVALID_EMAIL');
    }

    return errors;
  }

  const sendForm = async () => {
    setBtnStatus(ButtonStatus.InProgress);

    try {
      const result = await putReservation(
        selectedUnit.value,
        1,
        values.customerName,
        values.customerLastName,
        values.email,
        values.phone,
        customLang,
        null,
        null,
        null,
        format(reservationDate, "yyyy-MM-dd'T'HH:mm:ss.SSS"),
        amount,
        [],
        null,
        null,
        'LINK',
        values.description,
        selectedTime,
        null,
        values.externalRefCode,
        welletContext,
        selectedCurrency.value,
      );
      setBtnStatus(ButtonStatus.ProgressFinished);
      setTimeout(() => {
        history.push(`/link-confirmation?reservationId=${result.reservationId}`);
      }, 1500);
    } catch (error) {
      setBtnStatus(ButtonStatus.Error);
    }
  };

  const handleContinue = () => {
    setErrorAmount(null);
    setErrorUnit(null);

    const errorMessage = t('amountMinError', { amount: currency.symbol + ' 5' });
    if (parseFloat(amount) < 5) {
      setErrorAmount(errorMessage);
    }
    if (selectedUnit === null) {
      setErrorUnit(t('unitError'));
    }

    if (amount < 5 || selectedUnit === null) return;

    getShow();
    setShowForm(true);
  };

  let { values, errors, handleChange, handleSubmit } = useForm(sendForm, validate);
  const customLang = values.language ? values.language : app.language;
  const expireTimes = [
    {
      value: 30,
      label: '30 ' + t('minutes'),
    },
    {
      value: 60,
      label: t('hours_one', { count: 1 }),
    },
    {
      value: 120,
      label: t('hours_many', { count: 2 }),
    },
    {
      value: 360,
      label: t('hours_many', { count: 6 }),
    },
    {
      value: 720,
      label: t('hours_many', { count: 12 }),
    },
    {
      value: 1440,
      label: t('hours_many', { count: 24 }),
    },
    {
      value: 2880,
      label: '2 ' + t('days_many'),
    },
    {
      value: 7200,
      label: '5 ' + t('days_many'),
    },
    {
      value: 14400,
      label: '10 ' + t('days_many'),
    },
  ];

  useEffect(() => {
    getUnits();
  }, []);

  useEffect(() => {
    if (currency && currenciesOptions.length > 0) {
      const newSelected = currencyOption(currency);
      if (!selectedCurrency || selectedCurrency.value !== newSelected.value) {
        setSelectedCurrency(newSelected);
      }
    }
  }, [currency, currenciesOptions, selectedCurrency?.value]);

  const additionalChargeDetails = [];
  if (selectedUnit?.additionalCharges && selectedUnit?.additionalCharges.length > 0) {
    selectedUnit.additionalCharges.forEach((charge) => {
      if (charge.chargeType === 'PERCENTAGE' && charge.applyOn === 'SUBTOTAL') {
        const percentage = charge.chargeTarget;
        const subtotal = amount;
        const chargeAmount = (percentage / 100.0) * subtotal;
        additionalChargeDetails.push({ ...charge, chargeAmount });
      }
    });
  }
  additionalChargeDetails.sort((a, b) => a.displayOrder - b.displayOrder);
  const totalAmount =
    amount + additionalChargeDetails.reduce((acc, charge) => acc + charge.chargeAmount, 0);
  const isLocked = currenciesOptions?.length === 1;
  const Amount = () => {
    return (
      <>
        <Header />
        <div className={!canMakeReservations ? 'censured-content' : ''}>
          <ContentApp>
            <div>
              <div>
                <div className='fw-semibold font-medium'>{t('chargeAmount')}</div>
                <div className={styles.inputContainer}>
                  <div className='row'>
                    <div className='col-auto px-0'>
                      <div className='keypad-amount-container text-50 line-height-1'>
                        <div className='keypad-amount'>
                          <Money value={amount} />
                        </div>
                      </div>
                      <div className='text-center mt-3'>
                        <GeneralSelectorPicker
                          options={currenciesOptions}
                          selectedValue={selectedCurrency || defaultCurrencyOption}
                          onChange={(e) => setSelectedCurrency(e)}
                          placeholder={'Moneda'}
                          lockIfSingle={isLocked}
                          styles={{
                            lineHeight: '46px',
                            padding: '0 12px',
                            margin: '0 auto',
                            display: 'inline-block',
                            paddingLeft: isLocked ? '12px' : '0px',
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {errorAmount && parseFloat(amount) < 5 && (
                  <div className='error-label-link text-center' style={{ position: 'relative' }}>
                    {errorAmount}
                  </div>
                )}
              </div>
              <div>
                <div className='mt-3'>
                  <GeneralSelectorPicker
                    options={units}
                    selectedValue={selectedUnit}
                    onChange={handleUnitChange}
                    placeholder={t('restaurant')}
                  />
                </div>
                <LinkAmountDetails
                  amount={amount}
                  currency={selectedCurrency}
                  additionalCharges={additionalChargeDetails}
                  totalAmount={totalAmount}
                />
                {errorUnit && !selectedUnit && (
                  <div
                    className='error-label text-center'
                    style={{ position: 'relative', marginTop: '10px' }}
                  >
                    {errorUnit}
                  </div>
                )}

                <Button
                  size='large'
                  text={t('continue')}
                  className='mt-3'
                  onClick={handleContinue}
                />
                <Numpad
                  initialValue={0}
                  onValueChange={(value) => setAmount(value)}
                  showValue={false}
                />
              </div>
            </div>
          </ContentApp>
        </div>
        {!canMakeReservations && (
          <>
            <div className={styles.censuredOverlay}>
              <div className={styles.censuredCard + ' censured-text'}>
                <div className='font-large bold mb-2'>{t('verificationPending')}</div>
                <div style={{ opacity: 0.9 }}>{t('continueWithThisActivity')}</div>
                <div className='mt-4'>
                  <Button
                    type='button'
                    size='small'
                    text={t('verifyAccount')}
                    color='#fff'
                    onClick={() => history.push('/account-verification')}
                  />
                </div>
              </div>
            </div>
            <div className={styles.censuredOverlay} />
          </>
        )}
      </>
    );
  };

  const Form = () => {
    return (
      <>
        <Header onClick={resetForm} />
        <ContentApp>
          <div className='d-flex align-items-center flex-column'>
            <div className={styles.containerLogo}>
              <img src={selectedUnit.img} alt='logo' />
            </div>
            <div className='font-large fw-semibold mt-3'>{selectedUnit.label}</div>
            <div className='font-smaller mt-1 text-secondary'>{t('paymentLink')}</div>
            <div className='keypad-amount-container text-50 line-height-1 mt-2'>
              <div className='keypad-amount'>
                <Money value={totalAmount} symbol={selectedCurrency.symbol} />
              </div>
            </div>
            <div className='font-smaller text-secondary mt-1'>{selectedCurrency.label}</div>
          </div>

          <div className='mt-3'>
            <ReservationCheckCustomerInfo
              handleChange={handleChange}
              values={values}
              errors={errors}
              phoneError={phoneError}
              setPhoneError={setPhoneError}
              countryCode={countryCode}
            />
          </div>

          <div className='mt-4'>
            <div className='fw-bold mb-3'>{t('linkExpiration')}</div>
            <GeneralSelectorPicker
              options={expireTimes}
              placeholder={t('hours', { count: 24 })}
              selectedValue={{ value: selectedTime }}
              onChange={handleExpireTime}
            />
          </div>

          <div className='bold  mt-4 mb-3'>{t('paymentLinkInformation')}</div>

          <div className={`field-border-container mt-4 ${errors.externalRefCode ? 'error' : ''}`}>
            <div className='field-border '>
              <div className='field-border-name'>{t('internalReference')}</div>
              <InputFormBorder
                color='#757575'
                name='externalRefCode'
                type='text'
                onChange={handleChange}
                value={values.externalRefCode}
                placeholder={t('internalReference')}
                customClass='border-none p-0'
              />
            </div>
            {errors.externalRefCode && (
              <div className='errorLabel mt-2'>{errors.externalRefCode}</div>
            )}
          </div>

          <div className={`field-border-container  mt-4 ${errors.description ? 'error' : ''}`}>
            <div className='field-border'>
              <div className='field-border-name'>{t('description')}</div>
              <InputFormBorder
                color='#757575'
                name='description'
                type='text'
                onChange={handleChange}
                value={values.description}
                placeholder={t('description')}
                customClass='border-none p-0'
              />
            </div>
            {errors.description && <div className='errorLabel mt-4'>{errors.description}</div>}
          </div>

          <ButtonProccessing
            status={btnStatus}
            className={'mt-4'}
            text={t('sendPaymentLink')}
            onClick={handleSubmit}
            arrow
            size='large'
            color='#000'
            backgroundColor='var(--color-primary)'
            iconColor='#000'
          />
        </ContentApp>
      </>
    );
  };

  return (
    <>
      {loading ? (
        <div className='loaderDiv'>
          <CircleSpinner
            className='spinner-app mx-auto'
            style={{ margin: 'auto' }}
            size={40}
            color='var(--color-font-primary)'
          />
        </div>
      ) : showForm ? (
        Form()
      ) : (
        Amount()
      )}
    </>
  );
};

const LinkAmountDetails = ({ amount, currency, additionalCharges, totalAmount }) => {
  if (!additionalCharges || additionalCharges.length === 0) return null;

  const renderAdditionalCharge = (charge) => {
    return (
      <div>
        <div className='row mt-2 fw-500'>
          <div className='col pr-0'>
            {charge.name} ({charge.chargeTarget.toFixed(2)} %):
          </div>
          <div className='col-auto'>
            <Money value={charge.chargeAmount} convert={false} symbolSize='14px' />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      <div>
        <div>
          <div className='row mt-4 fw-500'>
            <div className='col pr-0'>Subtotal:</div>
            <div className='col-auto'>
              <Money value={amount} convert={false} symbolSize='14px' />
            </div>
          </div>
        </div>
        {additionalCharges.map((charge, index) => (
          <div key={index}>{renderAdditionalCharge(charge)}</div>
        ))}
        <div className='row fw-600 mt-2'>
          <div className='col pr-0'>Total:</div>
          <div className='col-auto'>
            <Money value={totalAmount} symbolSize='17.6px' />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Form;
